import React, { useEffect, useState } from "react";
import { Plus, Minus, ShoppingBag } from "lucide-react";
import { api } from "../lib/api";
import { inr } from "../lib/currency";
import { isOfferActive } from "../lib/offers";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const categories = [
  { id: "all", label: "All" },
  { id: "ghee", label: "Ghee" },
  { id: "milk", label: "Milk" },
  { id: "makhan", label: "Makhan" },
  { id: "oil", label: "Oil" },
  { id: "vegetables", label: "Vegetables" },
  { id: "mushroom", label: "Mushroom" },
  { id: "spices", label: "Spices" },
  { id: "seeds", label: "Seeds" },
  { id: "aata", label: "Aata" },
  { id: "dryfruits", label: "Dry Fruits" },
  { id: "coconut", label: "Coconut" },
  { id: "aachar", label: "Aachar" },
  { id: "tea", label: "Tea" },
  { id: "rajasthani", label: "Rajasthani Dry Veg" },
  { id: "laddu", label: "Laddu" },
];

const TAGLINES = {
  all: "Every jar, bottle and bunch — packed the day it leaves the farm.",
  ghee: "Hand-churned bilona, slow-simmered — the way grandmother did it.",
  milk: "Bottled at dawn from grass-fed cows, buffalo and goats.",
  makhan: "Fresh white butter, churned by hand every morning.",
  oil: "Cold-pressed on a wooden ghani, unrefined, farmhouse-strong.",
  vegetables: "Harvested this morning, on your doorstep tomorrow.",
  mushroom: "Farm-fresh khumbi — button, oyster, shiitake and rare gucchi.",
  spices: "Whole, sun-cured, regional — grind fresh for real warmth.",
  seeds: "Cold-stored, oil-rich, roast-ready — til, alsi, kaddu.",
  aata: "Chakki-fresh, heritage grains — soft rotis guaranteed.",
  dryfruits: "Sun-cured almonds, cashews, kishmish and Medjool dates.",
  coconut: "Sun-ripened Kerala nariyal — whole, powder, water and khopra.",
  aachar: "Sun-cured, small-batch — grandmother's pickle recipes in a jar.",
  tea: "Loose-leaf chai, kahwa and herbal blends — brew slow, sip warm.",
  rajasthani: "Ker, sangri, kachri, kasuri methi — the soul of a Marwari kitchen.",
  laddu: "Ghee-soaked, festive, hand-rolled — Diwali in every bite.",
};

// Subcategory strips shown when a parent is active
const SUBCATEGORIES = {
  ghee: [
    { id: "all", label: "All Ghee" },
    { id: "cow", label: "Cow Ghee" },
    { id: "buffalo", label: "Buffalo Ghee" },
  ],
  milk: [
    { id: "all", label: "All Milk" },
    { id: "cow", label: "Cow Milk" },
    { id: "buffalo", label: "Buffalo Milk" },
    { id: "goat", label: "Goat Milk" },
  ],
  rajasthani: [
    { id: "all", label: "All" },
    { id: "ker", label: "Ker" },
    { id: "sangri", label: "Sangri" },
    { id: "kachri", label: "Kachri" },
    { id: "kasuri_methi", label: "Kasuri Methi" },
    { id: "papad", label: "Rajasthani Papad" },
  ],
};

export const Shop = () => {
  const [active, setActive] = useState("all");
  const [sub, setSub] = useState("all");
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const { addItem } = useCart();

  useEffect(() => {
    const catHandler = (e) => {
      setActive(e.detail);
      setSub("all");
    };
    const searchHandler = (e) => setQuery(e.detail || "");
    window.addEventListener("set-category", catHandler);
    window.addEventListener("search-products", searchHandler);
    return () => {
      window.removeEventListener("set-category", catHandler);
      window.removeEventListener("search-products", searchHandler);
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products`)
      .then((res) => setAllProducts(res.data))
      .catch(() => toast.error("Could not load products"))
      .finally(() => setLoading(false));
  }, []);

  const q = query.trim().toLowerCase();
  // Search cuts across all categories. Category tabs only apply when no search.
  let visible = q
    ? allProducts.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q)
      )
    : active === "all"
    ? allProducts
    : allProducts.filter((p) => p.category === active);

  if (!q && sub !== "all" && SUBCATEGORIES[active]) {
    visible = visible.filter((p) => p.subcategory === sub);
  }

  const subs = !q ? SUBCATEGORIES[active] : null;

  return (
    <section id="shop" data-testid="shop-section" className="py-20 md:py-28 bg-background">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="font-mono-ui text-xs uppercase tracking-widest text-accent mb-3">
              — 01 / The Pantry
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-semibold tracking-tight leading-tight capitalize">
              {active === "all"
                ? "Shop the harvest"
                : categories.find((c) => c.id === active)?.label || "Shop"}
            </h2>
            <p
              data-testid="category-tagline"
              className="mt-3 text-base text-muted-foreground max-w-xl italic"
            >
              {TAGLINES[active] || TAGLINES.all}
            </p>
          </div>
          <p className="text-muted-foreground max-w-md">
            Ordered by season, priced by weight. Every jar and bunch is packed
            the day it leaves the farm.
          </p>
        </div>

        {subs && (
          <div
            data-testid="subcategory-strip"
            className="flex items-center gap-2 mb-8 pb-4 border-b border-border/60"
          >
            <span className="font-mono-ui text-[10px] uppercase tracking-widest text-muted-foreground shrink-0 mr-1">
              {categories.find((c) => c.id === active)?.label}
              <span className="mx-2 text-accent">|</span>
            </span>
            {subs.map((s) => (
              <button
                key={s.id}
                data-testid={`subcategory-${active}-${s.id}`}
                onClick={() => setSub(s.id)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  sub === s.id
                    ? "bg-accent text-accent-foreground"
                    : "bg-transparent border border-border text-foreground/75 hover:bg-secondary"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div
            data-testid="shop-empty-state"
            className="text-center py-20 border border-dashed border-border rounded-2xl"
          >
            <p className="font-display text-2xl">No matches for "{query}"</p>
            <p className="text-sm text-muted-foreground mt-2">
              Try a different word or browse the categories above.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {visible.map((p) => (
              <ProductCard key={p.id} product={p} onAdd={addItem} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const ProductCard = ({ product, onAdd }) => {
  const hasOffer = isOfferActive(product);
  const finalPrice = hasOffer
    ? product.price * (1 - product.discount_percent / 100)
    : product.price;
  const [qty, setQty] = useState(1);
  const { setOpen: setCartOpen } = useCart();

  const addToCart = () => {
    for (let i = 0; i < qty; i++) onAdd({ ...product, price: finalPrice });
    toast.success(`${qty} × ${product.name} added to your bag`);
    setQty(1);
  };

  const buyNow = () => {
    for (let i = 0; i < qty; i++) onAdd({ ...product, price: finalPrice });
    setCartOpen(true);
    setQty(1);
  };

  return (
    <article
      data-testid={`product-card-${product.id}`}
      className="group relative bg-card rounded-2xl overflow-hidden border border-border hover-lift flex flex-col"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-muted">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        {product.badge && !hasOffer && (
          <span className="absolute top-3 left-3 px-2.5 py-1 bg-accent text-accent-foreground text-[10px] font-mono-ui uppercase tracking-widest rounded-full">
            {product.badge}
          </span>
        )}
        {hasOffer && (
          <span
            data-testid={`product-offer-${product.id}`}
            className="absolute top-3 left-3 px-2.5 py-1 bg-destructive text-destructive-foreground text-[10px] font-mono-ui uppercase tracking-widest rounded-full shadow-lg"
          >
            {product.offer_label || `${Math.round(product.discount_percent)}% OFF`}
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-display text-lg font-semibold leading-tight">
            {product.name}
          </h3>
          <div className="text-right shrink-0">
            {hasOffer && (
              <div className="font-mono-ui text-xs text-muted-foreground line-through leading-tight">
                {inr(product.price)}
              </div>
            )}
            <div
              className={`font-mono-ui text-sm ${
                hasOffer ? "text-destructive font-semibold" : "text-primary"
              }`}
            >
              {inr(finalPrice)}
            </div>
          </div>
        </div>
        <div className="mt-1 text-xs font-mono-ui text-muted-foreground uppercase tracking-wider">
          {product.unit}
        </div>
        <p className="mt-2 text-sm text-muted-foreground line-clamp-2 flex-1">
          {product.description}
        </p>

        <div className="mt-4 flex items-center gap-2">
          <div className="inline-flex items-center border border-border rounded-full h-9 shrink-0">
            <button
              data-testid={`qty-dec-${product.id}`}
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              className="w-9 h-9 flex items-center justify-center hover:bg-muted rounded-l-full"
              aria-label="Decrease quantity"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span
              data-testid={`qty-value-${product.id}`}
              className="w-8 text-center text-sm font-mono-ui"
            >
              {qty}
            </span>
            <button
              data-testid={`qty-inc-${product.id}`}
              onClick={() => setQty((q) => q + 1)}
              className="w-9 h-9 flex items-center justify-center hover:bg-muted rounded-r-full"
              aria-label="Increase quantity"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={addToCart}
            className="flex-1 h-9 rounded-full bg-primary text-primary-foreground text-xs font-semibold uppercase tracking-widest hover:bg-accent hover:text-accent-foreground transition-colors flex items-center justify-center gap-1.5"
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            Add to Cart
          </button>
        </div>
        <button
          data-testid={`buy-now-${product.id}`}
          onClick={buyNow}
          className="mt-2 h-9 rounded-full border border-accent text-foreground text-xs font-semibold uppercase tracking-widest hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          Buy Now &rarr; Checkout
        </button>
      </div>
    </article>
  );
};
